import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { useLocation } from 'react-router-dom';
import ReportTabLayout from '../common/ReportTabLayout';
import ReportsPagination from '../common/ReportsPagination';
import ReportsEmptyState from '../common/ReportsEmptyState';
import ReportsErrorState from '../common/ReportsErrorState';
import ReportsFilterBar from '../common/ReportsFilterBar';
import useReportFilters from '../common/useReportFilters';
import { fetchLedger, fetchBillDetail } from '../../../services/backendAPI';
import LedgerTransactionDialog from './LedgerTransactionDialog';
import LedgerMobileList from './LedgerMobileList';
import LedgerTable from './LedgerTable';

const LedgerDetails = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  
  // Table data state
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [billDetails, setBillDetails] = useState(null);
  const [loadingBillDetails, setLoadingBillDetails] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const filterProps = useReportFilters({
    defaultPreset: 'week',
    onFiltersApplied: (filters) => {
      setPage(0);
      fetchLedgerData(0, rowsPerPage, filters);
    },
  });
  const { startDate, endDate, handleCustomDateApply, getFilterState } = filterProps;

  // `filters` is a full snapshot from useReportFilters; defaults to the
  // current committed state for page / rows-per-page changes.
  const fetchLedgerData = async (newPage, newRowsPerPage, filters = getFilterState()) => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await fetchLedger({
        start_date: format(filters.startDate, 'yyyy-MM-dd'),
        end_date: format(filters.endDate, 'yyyy-MM-dd'),
        page: newPage + 1,
        page_size: newRowsPerPage,
      });
      // An array (even an empty one) is a successful response; anything else
      // is a failure and must not be shown as "no entries".
      if (Array.isArray(response?.data)) {
        setLedgerData(response.data);
        setTotalCount(response.total_count || 0);
      } else {
        throw new Error(response?.error || 'Failed to fetch ledger data');
      }
    } catch (error) {
      console.error('Error fetching ledger data:', error);
      setLedgerData([]);
      setTotalCount(0);
      setLoadError(error.message || 'Error fetching ledger data');
      enqueueSnackbar(error.message || 'Error fetching ledger data', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial load — honour a deep-linked date range (e.g. navigated in from
  // elsewhere with `state.filters`) if present, otherwise the default preset.
  useEffect(() => {
    if (location.state?.filters) {
      const { startDate: sd, endDate: ed } = location.state.filters;
      handleCustomDateApply(new Date(sd), new Date(ed));
    } else {
      fetchLedgerData(0, rowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchLedgerData(newPage, rowsPerPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchLedgerData(0, newRowsPerPage);
  };

  const handleTransactionClick = async (transaction) => {
    setSelectedTransaction(transaction);
    // Drop the previous row's details so a failed fetch can't leave them
    // rendered under a different transaction's header.
    setBillDetails(null);
    if (isMobile) {
      setIsDrawerOpen(true);
    }
    
    // Fetch bill details if it's a bill transaction
    if (transaction.ledger_type === 'BILL') {
      setLoadingBillDetails(true);
      try {
        const response = await fetchBillDetail({
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd'),
          page: 1,
          page_size: 20,
          bill_id: transaction.bill_id
        });
        if (response?.data) {
          setBillDetails(response.data);
        }
      } catch (error) {
        console.error('Error fetching bill details:', error);
        setBillDetails(null);
        enqueueSnackbar('Error fetching bill details', { 
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 3000
        });
      } finally {
        setLoadingBillDetails(false);
      }
    }
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedTransaction(null);
  };

  // Identical in the mobile drawer and the desktop dialog — defined once so
  // the two can't drift apart.

  return (
    <ReportTabLayout>
      
      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1, sm: 1.25 },
          mb: isMobile ? 1 : 2,
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid ' + theme.palette.divider,
        }}
      >
        <ReportsFilterBar
          filterProps={filterProps}
          showFilterDrawerButton={false}
        />
      </Paper>
      {/* Results */}
      <Paper sx={{ 
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        width: '100%'
      }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : loadError ? (
            <ReportsErrorState
              title="Couldn't Load Ledger"
              message={loadError}
              onRetry={() => fetchLedgerData(page, rowsPerPage)}
            />
          ) : ledgerData.length === 0 ? (
            <ReportsEmptyState
              icon={AssessmentIcon}
              title="No Ledger Entries Found"
              message="No transactions found for the selected date range."
              iconSize={64}
              padding={6}
            />
          ) : isMobile ? (
            <LedgerMobileList
              ledgerData={ledgerData}
              onViewDetails={handleTransactionClick}
            />
          ) : (
            <LedgerTable
              ledgerData={ledgerData}
              onRowClick={handleTransactionClick}
            />
          )}
      </Paper>

      <ReportsPagination
          show={ledgerData.length > 0}
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />

      <LedgerTransactionDialog
        transaction={selectedTransaction}
        isMobile={isMobile}
        drawerOpen={isDrawerOpen}
        onCloseDrawer={handleCloseDrawer}
        onCloseDialog={() => setSelectedTransaction(null)}
        billDetails={billDetails}
        loadingBillDetails={loadingBillDetails}
      />
    </ReportTabLayout>
  );
};

export default LedgerDetails;